// src/components/BillingSettings.js

import React, { useState, useEffect } from 'react';
import api from '../api';
import './styles/BillingSettings.css';

function BillingSettings({ user_id, accountType = "couple" }) {
  const [billingInfo, setBillingInfo]       = useState(null);
  const [loading, setLoading]               = useState(true);
  const [cancelLoading, setCancelLoading]   = useState(false);
  const [showPlanModal, setShowPlanModal]   = useState(false);
  const [newPlan, setNewPlan]               = useState("");

  // Fetch billing info on component mount
  useEffect(() => {
    const fetchBillingInfo = async () => {
      setLoading(true);
      try {
        const res = await api.get('/get_billing_info/', {
          params: { user_id }
        });
        const data = res.data;
        if (data.status === 'success') {
          setBillingInfo(data.billing);
          if (data.billing.subscription?.plan) {
            setNewPlan(data.billing.subscription.plan);
          }
        } else {
          console.error('Error retrieving billing info:', data.message);
          setBillingInfo(null);
        }
      } catch (error) {
        console.error('Network or server error fetching billing info:', error);
        setBillingInfo(null);
      }
      setLoading(false);
    };

    fetchBillingInfo();
  }, [user_id]);

  const handleCancelSubscription = async () => {
    if (!window.confirm("Are you sure you want to cancel your subscription?"))
      return;

    setCancelLoading(true);
    try {
      const res = await api.post('/subscription_cancellation/', { user_id });
      const result = res.data;
      if (result.status === 'success') {
        alert("Subscription cancelled successfully.");
        setBillingInfo(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            subscription: {
              ...prev.subscription,
              status: result.subscription?.status || 'canceled'
            }
          };
        });
      } else {
        alert("Failed to cancel subscription.");
      }
    } catch (error) {
      console.error("Error cancelling subscription:", error);
      alert("Error cancelling subscription.");
    }
    setCancelLoading(false);
  };

  const handleUpdatePaymentMethod = async () => {
    try {
      const res = await api.post('/update_payment_method', {
        user_id,
        account_type: accountType
      });
      const data = res.data;
      if (data.status === "success" && data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else {
        alert("Error updating payment method.");
      }
    } catch (error) {
      console.error("Error updating payment method:", error);
      alert("Error updating payment method.");
    }
  };

  const handlePlanChangeConfirm = async () => {
    if (!newPlan) return;

    try {
      const res = await api.post(
        '/stripe_session',
        null,
        { params: { user_id, plan: newPlan, account_type: accountType } }
      );
      const data = res.data;
      if (data.status === "success" && data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else {
        alert("Error initiating plan update.");
      }
    } catch (error) {
      console.error("Error initiating plan update:", error);
      alert("Error initiating plan update.");
    }
  };

  if (loading) {
    return <div className="billing-settings">Loading billing information...</div>;
  }

  if (!billingInfo) {
    return (
      <div className="billing-settings">
        <h2>Billing Settings</h2>
        <p>No billing information available or an error occurred.</p>
      </div>
    );
  }

  const { subscription, payment_method, upcoming_invoice, invoices } = billingInfo;
  const nextBillingDate = upcoming_invoice?.next_payment_attempt
    ? new Date(upcoming_invoice.next_payment_attempt * 1000).toLocaleDateString()
    : subscription.end_date
      ? new Date(subscription.end_date * 1000).toLocaleDateString()
      : 'N/A';

  const currentPlan = subscription?.plan || null;
  const availablePlans = currentPlan
    ? (accountType === "couple"
        ? ["essentials", "standard", "premium"]
        : ["standard", "premium"])
    : (accountType === "couple"
        ? ["trial", "essentials", "standard", "premium"]
        : ["trial", "standard", "premium"]);

  return (
    <div className="billing-settings">
      <h2>Billing Settings</h2>

      <div className="billing-item">
        <label>Subscription Status:</label>
        <p>{subscription.status || 'Not subscribed'}</p>
      </div>

      <div className="billing-item">
        <label>Current Plan:</label>
        <p>{currentPlan || 'N/A'}</p>
        <button className="change-plan-btn" onClick={() => setShowPlanModal(true)}>
          Change Plan
        </button>
      </div>

      <div className="billing-item">
        <label>Payment Method:</label>
        {payment_method ? (
          <p>
            {payment_method.brand.toUpperCase()} **** **** **** {payment_method.last4}
            <br />
            Expires {payment_method.exp_month}/{payment_method.exp_year}
          </p>
        ) : (
          <p>No payment method on file</p>
        )}
      </div>

      <div className="billing-item">
        <label>Next Billing Date:</label>
        <p>{nextBillingDate}</p>
      </div>

      {invoices?.length > 0 && (
        <div className="billing-item invoice-history">
          <h3>Past Invoices</h3>
          <table>
            <thead>
              <tr>
                <th>Invoice ID</th>
                <th>Amount Paid</th>
                <th>Status</th>
                <th>Period Start</th>
                <th>Period End</th>
                <th>Invoice PDF</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => (
                <tr key={inv.invoice_id}>
                  <td>{inv.invoice_id}</td>
                  <td>${(inv.amount_paid / 100).toFixed(2)}</td>
                  <td>{inv.status}</td>
                  <td>
                    {inv.period_start
                      ? new Date(inv.period_start * 1000).toLocaleDateString()
                      : 'N/A'}
                  </td>
                  <td>
                    {inv.period_end
                      ? new Date(inv.period_end * 1000).toLocaleDateString()
                      : 'N/A'}
                  </td>
                  <td>
                    {inv.invoice_pdf ? (
                      <a href={inv.invoice_pdf} target="_blank" rel="noopener noreferrer">
                        Download
                      </a>
                    ) : (
                      'N/A'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="billing-actions">
        {['active', 'trialing'].includes(subscription.status) && (
          <button
            className="cancel-subscription-btn"
            onClick={handleCancelSubscription}
            disabled={cancelLoading}
          >
            {cancelLoading ? 'Cancelling...' : 'Cancel Subscription'}
          </button>
        )}
        <button className="update-payment-btn" onClick={handleUpdatePaymentMethod}>
          Update Payment Method
        </button>
      </div>

      {showPlanModal && (
        <div className="modal-overlay" onClick={() => setShowPlanModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Select a New Plan</h3>
            <p>Your current plan is: <strong>{currentPlan || 'N/A'}</strong></p>
            <div className="plan-options">
              {availablePlans.map(plan => (
                <div key={plan} className="plan-option">
                  <input
                    type="radio"
                    id={`plan-${plan}`}
                    name="plan"
                    value={plan}
                    checked={newPlan === plan}
                    onChange={e => setNewPlan(e.target.value)}
                    disabled={plan === currentPlan}
                  />
                  <label htmlFor={`plan-${plan}`}>
                    {plan.charAt(0).toUpperCase() + plan.slice(1)}{plan === currentPlan ? " (Current)" : ""}
                  </label>
                </div>
              ))}
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowPlanModal(false)}>Cancel</button>
              <button onClick={handlePlanChangeConfirm}>Confirm Plan Change</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BillingSettings;
